import React, { useState } from 'react';

import { saveCommunitiesEmailNotificationRecipients } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';
import SettingsPage from 'components/Settings/SettingsPage';
import { useAppSelector } from 'store/hook';
import { CommunityPreferenceNotification, CommunityPreferenceType, ReviewAlertPreference } from 'types/communities';

import CommunityReviewAlertCategoriesLabel from './CommunityReviewAlertCategoriesLabel';
import CommunityReviewAlertsRecipientsModal from './CommunityReviewAlertsRecipientsModal';
import CommunityReviewAlertsStatus from './CommunityReviewAlertsStatus';
import { CommunityReviewAlertMode, CommunityReviewAlertScope } from './constants';
import { CommunityReviewAlertScopeSettings } from './types';
import { COMMUNITY_REVIEW_ALERTS, COMMUNITY_SETTINGS_PAGE_TYPE, EMAIL_NOTIFICATION_TYPE } from '../../constants';

const CommunityReviewAlerts = () => {
  const { showMessage } = useRootContext();
  const reviewCategories = useAppSelector((state) => state.global.reviewCategories);
  const allReviewCategoryIds = reviewCategories.map((category) => category.id);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [communitiesToModify, setCommunitiesToModify] = useState<CommunityPreferenceType[]>([]);
  const [refetchCommunities, setRefetchCommunities] = useState<() => void | undefined>();
  const [clearSelection, setClearSelection] = useState<() => void | undefined>();

  const renderExtraColumns = (item: CommunityPreferenceType) => [
    item.preferences?.community_review_alert_notifications
      ? (
          <CommunityReviewAlertCategoriesLabel
            preferences={item.preferences?.community_review_alert_preferences}
            allReviewCategoryIds={allReviewCategoryIds}
          />
        )
      : '',
    item.preferences?.competitor_review_alert_notifications
      ? (
          <CommunityReviewAlertCategoriesLabel
            preferences={item.preferences?.competitor_review_alert_preferences}
            allReviewCategoryIds={allReviewCategoryIds}
          />
        )
      : '',
  ];

  const buildPreferencesPayload = (
    settings: CommunityReviewAlertScopeSettings
  ): Omit<ReviewAlertPreference, 'id' | 'mode'>[] => {
    if (settings.mode === CommunityReviewAlertMode.OVERALL) {
      return [
        {
          number_of_reviews: settings.overall.number_of_reviews,
          positive: settings.overall.positive,
          within_months: settings.overall.within_months,
          review_category_id: null,
        },
      ];
    }

    return settings.selected_category_ids.map((reviewCategoryId) => {
      const categorySettings = settings.category_settings[reviewCategoryId] ?? settings.overall;

      return {
        number_of_reviews: categorySettings.number_of_reviews,
        positive: categorySettings.positive,
        within_months: categorySettings.within_months,
        review_category_id: reviewCategoryId,
      };
    });
  };

  const buildScopePreferences = ({
    scope,
    alertFlags,
    alertModeSettings,
  }: {
    scope: CommunityReviewAlertScope;
    alertFlags: Record<CommunityReviewAlertScope, boolean>;
    alertModeSettings: Record<CommunityReviewAlertScope, CommunityReviewAlertScopeSettings>;
  }) => {
    if (!alertFlags[scope]) {
      return [];
    }

    return buildPreferencesPayload(alertModeSettings[scope]);
  };

  const showCommunitiesEditModal = (items: any[]) => {
    setCommunitiesToModify(items);
    setIsModalOpen(true);
  };

  const savePreferenceRecipients = (
    notificationType: number,
    newStatus: number,
    preferenceNotifications: CommunityPreferenceNotification[],
    alertModeSettings: Record<CommunityReviewAlertScope, CommunityReviewAlertScopeSettings>,
    alertFlags: Record<CommunityReviewAlertScope, boolean>
  ) => {
    setLoading(true);
    const activeScope =
      notificationType === EMAIL_NOTIFICATION_TYPE.COMMUNITY_REVIEW_ALERTS
        ? CommunityReviewAlertScope.COMMUNITY
        : CommunityReviewAlertScope.COMPETITOR;
    const activePreferences = buildScopePreferences({
      scope: activeScope,
      alertFlags,
      alertModeSettings,
    });

    const payload = {
      communities: preferenceNotifications.map((community) => ({
        ...community,
        community_review_alert_preferences: activePreferences,
      })),
      email_notifications_enabled: newStatus,
    };

    saveCommunitiesEmailNotificationRecipients(notificationType, payload)
      .then(() => {
        showMessage('success', 'All selected communities updated successfully');
        if (refetchCommunities) refetchCommunities();
        if (clearSelection) clearSelection();
      })
      .catch(() => {
        showMessage('error', 'Failed to update one or more communities!');
      })
      .finally(() => {
        setLoading(false);
        setIsModalOpen(false);
      });
  };

  return (
    <>
      <SettingsPage
        filterOptions={Object.values(COMMUNITY_REVIEW_ALERTS.statusFilterElements)}
        statusFilterWidth={220}
        getItemStatusComponent={(item: CommunityPreferenceType) => <CommunityReviewAlertsStatus item={item} />}
        showCommunitiesEditModal={showCommunitiesEditModal}
        settingsPageType={COMMUNITY_SETTINGS_PAGE_TYPE.COMMUNITY_REVIEW_ALERTS}
        extraHeaders={['COMMUNITY ALERTS', 'COMPETITOR ALERTS']}
        renderExtraColumns={renderExtraColumns}
        setRefetchCommunities={setRefetchCommunities}
        setClearSelection={setClearSelection}
        notificationType={EMAIL_NOTIFICATION_TYPE.COMMUNITY_REVIEW_ALERTS}
      />
      <CommunityReviewAlertsRecipientsModal
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        loading={loading}
        title={COMMUNITY_REVIEW_ALERTS.title}
        communitiesToModify={communitiesToModify}
        onSave={savePreferenceRecipients}
      />
    </>
  );
};

export default CommunityReviewAlerts;
